import React, { useState, useMemo } from 'react';
import ExcelJS from 'exceljs';
import api from '../services/api';
import {
    Check, Plus, X, Search, Zap, RotateCcw, Lock, Download, ChevronDown,
} from 'lucide-react';

/* ── colour tokens ────────────────────────────────────────────────────────── */
const INK   = '#08302F';
const TEAL  = '#0E5C57';
const PAPER = '#F3F6F4';
const CARD  = '#FFFFFF';
const RULE  = '#D8E3DE';
const LIME  = '#77BE3F';
const AMBER = '#E7A233';
const MUTED = '#6B807B';

const OPERATORS = [
    { value: '=',  label: '= equals'    },
    { value: '!=', label: '≠ not equal' },
    { value: '>',  label: '> greater'   },
    { value: '<',  label: '< less'      },
    { value: '>=', label: '≥ or equal'  },
    { value: '<=', label: '≤ or equal'  },
];

const MISSING_OPTS = [
    { value: '',                    label: 'Drop missing cases',  hint: 'Default. Rows with any gap are excluded.' },
    { value: 'Replace with Mean',   label: 'Replace with mean',   hint: 'Keeps base size, flattens variance.'     },
    { value: 'Replace with Median', label: 'Replace with median', hint: 'Robust to outliers.'                     },
];

const METHODS = [
    { id: 'linear_regression', name: 'Linear regression', note: 'Scale outcome'        },
    { id: 'kruskal',           name: 'Kruskal analysis',  note: 'Correlated predictors' },
];

function shortLabel(label = '') {
    const stripped = label.replace(/^(Q\d+[a-z]?|S\d+)\s+/i, '');
    return stripped.split(' ').slice(0, 3).join(' ');
}

/* ── primitive sub-components ─────────────────────────────────────────────── */
function Eyebrow({ index, children, hint }) {
    return (
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 14 }}>
            <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
                           letterSpacing: '0.15em', color: LIME, flexShrink: 0 }}>
                {index}
            </span>
            <span style={{ fontSize: 13, fontWeight: 700, color: INK, letterSpacing: '-0.01em' }}>
                {children}
            </span>
            {hint && (
                <span style={{ fontSize: 11, color: MUTED, marginLeft: 'auto' }}>{hint}</span>
            )}
        </div>
    );
}

function Panel({ children, style = {} }) {
    return (
        <section style={{ background: CARD, border: `1px solid ${RULE}`,
                          borderRadius: 12, padding: '18px 20px', ...style }}>
            {children}
        </section>
    );
}

function StyledSelect({ value, onChange, children, disabled = false }) {
    return (
        <div style={{ position: 'relative' }}>
            <select
                value={value}
                onChange={e => onChange(e.target.value)}
                disabled={disabled}
                style={{
                    width: '100%', appearance: 'none', background: disabled ? '#f3f4f6' : PAPER,
                    border: `1px solid ${RULE}`, borderRadius: 8, padding: '9px 34px 9px 12px',
                    fontSize: 13, color: disabled ? '#9ca3af' : INK, outline: 'none',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                }}
            >
                {children}
            </select>
            <ChevronDown size={14} style={{ position: 'absolute', right: 10, top: '50%',
                                           transform: 'translateY(-50%)', color: MUTED,
                                           pointerEvents: 'none' }} />
        </div>
    );
}

function MethodBtn({ id, name, note, active, onClick }) {
    return (
        <button type="button" onClick={onClick} style={{
            background: active ? INK : PAPER, border: `1px solid ${active ? INK : RULE}`,
            borderRadius: 10, padding: '12px 14px', textAlign: 'left', cursor: 'pointer',
            transition: 'all 0.15s',
        }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: active ? '#fff' : INK }}>{name}</div>
            <div style={{ fontSize: 11, marginTop: 2, color: active ? '#8FB0AA' : MUTED }}>{note}</div>
        </button>
    );
}

/* ── main component ───────────────────────────────────────────────────────── */
const DriverAnalysisForm = ({ columns = [], binaryColumns = [], valueLabels = {}, fileId }) => {
    const [analysisType, setAnalysisType]   = useState('linear_regression');
    const [dependent, setDependent]         = useState('');
    const [nanAction, setNanAction]         = useState('');
    const [filter, setFilter]               = useState('');
    const [filters, setFilters]             = useState([]);
    const [independent, setIndependent]     = useState([]);
    const [query, setQuery]                 = useState('');
    const [isRunning, setIsRunning]         = useState(false);
    const [results, setResults]             = useState([]);
    const [sample, setSample]               = useState(null);
    const [errorMessage, setErrorMessage]   = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    /* derived */
    const ready = Boolean(dependent) && independent.length >= 2;

    const shownCols = useMemo(() => {
        const q = query.trim().toLowerCase();
        return columns.filter(c =>
            c.name !== dependent && (!q || (c.label || c.name).toLowerCase().includes(q))
        );
    }, [columns, dependent, query]);

    const depVar  = columns.find(c => c.name === dependent);
    const hasResults = results.length > 0;

    /* predictor toggle */
    const toggle = id =>
        setIndependent(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

    /* filter helpers */
    const addFilter    = () => setFilters(f => [...f, { key: Date.now(), column: '', operator: '=', values: [] }]);
    const removeFilter = key => setFilters(f => f.filter(x => x.key !== key));
    const updateFilter = (key, field, val) =>
        setFilters(f => f.map(x => x.key === key ? { ...x, [field]: val } : x));
    const toggleChip   = (key, val) =>
        setFilters(f => f.map(x => {
            if (x.key !== key) return x;
            const s = String(val);
            return { ...x, values: x.values.includes(s) ? x.values.filter(v => v !== s) : [...x.values, s] };
        }));

    /* reset */
    const reset = () => {
        setAnalysisType('linear_regression'); setDependent(''); setNanAction('');
        setFilter(''); setFilters([]); setIndependent([]); setQuery('');
        setResults([]); setSample(null); setErrorMessage(''); setSuccessMessage('');
    };

    /* run analysis */
    const handleSubmit = async () => {
        setResults([]); setErrorMessage(''); setSuccessMessage('Running…');
        setIsRunning(true);
        if (!fileId) { setErrorMessage('File ID missing. Select a file in step 2.'); setIsRunning(false); return; }
        try {
            const validFilters = filters
                .filter(f => f.column && f.operator && f.values.length > 0)
                .map(f => ({ column: f.column, operator: f.operator, value: f.values }));
            const payload = {
                file_id: fileId, dependent, independent,
                nan_action: nanAction || null, analysis_type: analysisType,
                binary_data: filter || null, filters: validFilters,
            };
            const res = await api.post('drivers/analyze/', payload, {
                headers: { Authorization: `Token ${localStorage.getItem('Token')}`, 'Content-Type': 'application/json' },
            });
            if (res.data.error) {
                setErrorMessage(res.data.error); setSuccessMessage('');
            } else {
                setResults(res.data.data); setSample(res.data.sample);
                setSuccessMessage(res.data.message || 'Analysis complete.'); setErrorMessage('');
            }
        } catch (e) {
            setErrorMessage(e.response?.data?.error || 'Analysis failed.'); setSuccessMessage('');
        } finally { setIsRunning(false); }
    };

    /* export */
    const handleExport = async () => {
        if (!results.length) return;
        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet('Driver Analysis');
        if (analysisType === 'linear_regression') {
            ws.columns = [
                { header: 'Driver',               key: 'Driver',              width: 40 },
                { header: 'Coefficient',           key: 'Coefficient',         width: 15 },
                { header: 'Absolute Coefficient',  key: 'Abs_Coeff',           width: 20 },
                { header: 'Coefficient (%)',        key: 'Coeff_Pct',           width: 15 },
                { header: 'R-Squared',             key: 'R_Squared',           width: 15 },
            ];
            results.forEach(r => ws.addRow({
                Driver: r.Driver || '-',
                Coefficient: r.Coefficient?.toFixed(2) || '-',
                Abs_Coeff: r['Absolute Coefficient']?.toFixed(2) || '-',
                Coeff_Pct: r['Coefficient (%)']?.toFixed(2) || '-',
                R_Squared: r['R-Squared']?.toFixed(4) || '-',
            }));
        } else {
            ws.columns = [
                { header: 'Driver',                           key: 'Driver',   width: 40 },
                { header: 'Kruskal Importance (%)',           key: 'Kruskal',  width: 22 },
                { header: 'Lower Confidence (0.025)',         key: 'Lower',    width: 22 },
                { header: 'Raw Kruskal',                      key: 'Raw',      width: 20 },
                { header: 'Upper Confidence (0.975)',         key: 'Upper',    width: 22 },
            ];
            results.forEach(r => ws.addRow({
                Driver: r.Driver || '-',
                Kruskal: r['Kruskal Importance (%)']?.toFixed(2) || '-',
                Lower: r['Lower Confidence Bound (0.025)']?.toFixed(4) || '-',
                Raw: r['Raw Kruskal Importance']?.toFixed(4) || '-',
                Upper: r['Upper Confidence Bound (0.975)']?.toFixed(4) || '-',
            }));
        }
        const buf = await wb.xlsx.writeBuffer();
        const a = document.createElement('a');
        a.href = URL.createObjectURL(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
        a.download = 'Driver_Analysis_Results.xlsx';
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
    };

    const totalFilters = filters.filter(f => f.column && f.values.length > 0).length + (filter ? 1 : 0);

    const checks = [
        { label: 'Method chosen',            done: true                   },
        { label: 'Outcome set',              done: Boolean(dependent)     },
        { label: 'Two or more drivers',      done: independent.length >= 2 },
    ];

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500&family=IBM+Plex+Sans:wght@400;500;600&display=swap');
                .daf-root, .daf-root button, .daf-root select, .daf-root input { font-family: 'IBM Plex Sans', system-ui, sans-serif; }
                .daf-root input:focus, .daf-root select:focus { outline: 2px solid #0E5C57; outline-offset: 1px; }
            `}</style>

            <div className="daf-root" style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>

                {/* ── Left column ─────────────────────────────────────── */}
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>

                    {/* 01 Method */}
                    <Panel>
                        <Eyebrow index="01" hint="Determines the coefficients you get back">Method</Eyebrow>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                            {METHODS.map(m => (
                                <MethodBtn key={m.id} {...m} active={analysisType === m.id}
                                           onClick={() => setAnalysisType(m.id)} />
                            ))}
                        </div>
                    </Panel>

                    {/* 02 Outcome */}
                    <Panel>
                        <Eyebrow index="02" hint="One variable">Outcome to explain</Eyebrow>
                        <StyledSelect value={dependent} onChange={v => {
                            setDependent(v);
                            setIndependent(p => p.filter(x => x !== v));
                        }}>
                            <option value="">Choose a variable…</option>
                            {columns.map(c => (
                                <option key={c.name} value={c.name}>{c.label || c.name}</option>
                            ))}
                        </StyledSelect>
                    </Panel>

                    {/* 03 Base & missing */}
                    <Panel>
                        <Eyebrow index="03" hint="Who stays in the base">Base &amp; missing data</Eyebrow>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
                            <div>
                                <div style={{ fontSize: 11, fontWeight: 600, color: MUTED, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                    Binary filter
                                </div>
                                <StyledSelect value={filter} onChange={setFilter} disabled={binaryColumns.length === 0}>
                                    <option value="">Everyone</option>
                                    {binaryColumns.map(c => (
                                        <option key={c.name} value={c.name}>{c.label || c.name}</option>
                                    ))}
                                </StyledSelect>
                            </div>
                            <div>
                                <div style={{ fontSize: 11, fontWeight: 600, color: MUTED, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                    Choose the NaN Handling
                                </div>
                                <StyledSelect value={nanAction} onChange={setNanAction}>
                                    {MISSING_OPTS.map(m => (
                                        <option key={m.value} value={m.value}>{m.label}</option>
                                    ))}
                                </StyledSelect>
                                <div style={{ fontSize: 11, color: MUTED, marginTop: 5 }}>
                                    {MISSING_OPTS.find(m => m.value === nanAction)?.hint}
                                </div>
                            </div>
                        </div>

                        {/* Data filters */}
                        <div style={{ borderTop: `1px dashed ${RULE}`, paddingTop: 16 }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                                <span style={{ fontSize: 11, fontWeight: 600, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                    Data filters
                                </span>
                                <button type="button" onClick={addFilter} style={{
                                    display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 500,
                                    color: TEAL, border: `1px solid ${RULE}`, background: 'transparent',
                                    borderRadius: 6, padding: '4px 10px', cursor: 'pointer',
                                }}>
                                    <Plus size={12} /> Add filter
                                </button>
                            </div>

                            {filters.length === 0 ? (
                                <p style={{ fontSize: 13, color: MUTED }}>No filters — every respondent is included.</p>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    {filters.map(f => {
                                        const vl = f.column ? (valueLabels[f.column] || []) : [];
                                        return (
                                            <div key={f.key} style={{ background: PAPER, borderRadius: 8,
                                                                       padding: '10px 12px', border: `1px solid ${RULE}` }}>
                                                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: vl.length || f.column ? 8 : 0 }}>
                                                    <div style={{ flex: 1, position: 'relative' }}>
                                                        <select value={f.column}
                                                            onChange={e => updateFilter(f.key, 'column', e.target.value)}
                                                            style={{ width: '100%', appearance: 'none', background: '#fff',
                                                                     border: `1px solid ${RULE}`, borderRadius: 6,
                                                                     padding: '6px 28px 6px 10px', fontSize: 12, color: INK, outline: 'none' }}>
                                                            <option value="">Select column…</option>
                                                            {columns.map(c => (
                                                                <option key={c.name} value={c.name}>{c.label || c.name}</option>
                                                            ))}
                                                        </select>
                                                        <ChevronDown size={12} style={{ position: 'absolute', right: 8, top: '50%',
                                                                                         transform: 'translateY(-50%)', color: MUTED,
                                                                                         pointerEvents: 'none' }} />
                                                    </div>
                                                    <div style={{ flexShrink: 0, position: 'relative' }}>
                                                        <select value={f.operator}
                                                            onChange={e => updateFilter(f.key, 'operator', e.target.value)}
                                                            style={{ appearance: 'none', background: '#fff',
                                                                     border: `1px solid ${RULE}`, borderRadius: 6,
                                                                     padding: '6px 24px 6px 8px', fontSize: 12, color: INK, outline: 'none' }}>
                                                            {OPERATORS.map(o => (
                                                                <option key={o.value} value={o.value}>{o.label}</option>
                                                            ))}
                                                        </select>
                                                        <ChevronDown size={12} style={{ position: 'absolute', right: 6, top: '50%',
                                                                                         transform: 'translateY(-50%)', color: MUTED,
                                                                                         pointerEvents: 'none' }} />
                                                    </div>
                                                    <button type="button" onClick={() => removeFilter(f.key)}
                                                        style={{ background: 'none', border: 'none', cursor: 'pointer',
                                                                 color: MUTED, padding: 4, borderRadius: 4, display: 'flex' }}>
                                                        <X size={14} />
                                                    </button>
                                                </div>
                                                {f.column && vl.length > 0 ? (
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                                                        {vl.map(v => {
                                                            const s = String(v.value);
                                                            const sel = f.values.includes(s);
                                                            return (
                                                                <button key={s} type="button"
                                                                    onClick={() => toggleChip(f.key, v.value)}
                                                                    style={{
                                                                        fontSize: 11, padding: '3px 9px', borderRadius: 999,
                                                                        border: `1px solid ${sel ? TEAL : RULE}`,
                                                                        background: sel ? TEAL : '#fff',
                                                                        color: sel ? '#fff' : INK, cursor: 'pointer',
                                                                    }}>
                                                                    {v.value} — {v.label}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                ) : f.column ? (
                                                    <input type="text"
                                                        value={(f.values[0]) || ''}
                                                        onChange={e => updateFilter(f.key, 'values', e.target.value ? [e.target.value] : [])}
                                                        placeholder="Enter value…"
                                                        style={{ width: '100%', background: '#fff', border: `1px solid ${RULE}`,
                                                                 borderRadius: 6, padding: '6px 10px', fontSize: 12, color: INK,
                                                                 outline: 'none', boxSizing: 'border-box' }} />
                                                ) : null}
                                                {f.values.length > 0 && (
                                                    <div style={{ fontSize: 11, color: TEAL, marginTop: 5 }}>
                                                        {f.values.length} value{f.values.length !== 1 ? 's' : ''} selected
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </Panel>

                    {/* 04 Drivers */}
                    <Panel>
                        <Eyebrow index="04" hint="Two or more required">Possible drivers</Eyebrow>

                        <div style={{ position: 'relative', marginBottom: 10 }}>
                            <Search size={13} style={{ position: 'absolute', left: 10, top: '50%',
                                                        transform: 'translateY(-50%)', color: MUTED }} />
                            <input type="text" value={query} onChange={e => setQuery(e.target.value)}
                                placeholder="Filter by name…"
                                style={{ width: '100%', paddingLeft: 32, paddingRight: 12, paddingTop: 8, paddingBottom: 8,
                                         fontSize: 13, background: PAPER, border: `1px solid ${RULE}`,
                                         borderRadius: 8, color: INK, outline: 'none', boxSizing: 'border-box' }} />
                        </div>

                        <div style={{ maxHeight: 280, overflowY: 'auto', border: `1px solid ${RULE}`, borderRadius: 8 }}>
                            {shownCols.length === 0 ? (
                                <p style={{ padding: '24px 16px', textAlign: 'center', fontSize: 13, color: MUTED }}>
                                    {query ? `Nothing matches "${query}".` : 'No variables available.'}
                                </p>
                            ) : shownCols.map((v, i) => {
                                const on = independent.includes(v.name);
                                return (
                                    <label key={v.name} style={{
                                        display: 'flex', alignItems: 'center', gap: 12,
                                        padding: '10px 14px', cursor: 'pointer',
                                        background: on ? '#F0F7EC' : CARD,
                                        borderTop: i === 0 ? 'none' : `1px solid ${RULE}`,
                                        transition: 'background 0.1s',
                                    }}>
                                        <input type="checkbox" checked={on} onChange={() => toggle(v.name)}
                                            style={{ width: 15, height: 15, accentColor: TEAL, cursor: 'pointer', flexShrink: 0 }} />
                                        <span style={{ flex: 1, fontSize: 13, color: INK }}>{v.label || v.name}</span>
                                        <span style={{ fontSize: 10, color: MUTED, fontFamily: "'IBM Plex Mono', monospace",
                                                        textTransform: 'uppercase', letterSpacing: '0.08em', flexShrink: 0 }}>
                                            {v.type || 'scale'}
                                        </span>
                                    </label>
                                );
                            })}
                        </div>

                        <div style={{ display: 'flex', gap: 16, marginTop: 10, fontSize: 12 }}>
                            <button type="button"
                                onClick={() => setIndependent(p =>
                                    Array.from(new Set([...p, ...shownCols.filter(c => c.name !== dependent).map(c => c.name)]))
                                )}
                                style={{ color: TEAL, background: 'none', border: 'none', cursor: 'pointer',
                                         textDecoration: 'underline dotted', textUnderlineOffset: 3, fontWeight: 500 }}>
                                Add all shown
                            </button>
                            {independent.length > 0 && (
                                <button type="button" onClick={() => setIndependent([])}
                                    style={{ color: MUTED, background: 'none', border: 'none', cursor: 'pointer',
                                             textDecoration: 'underline dotted', textUnderlineOffset: 3, fontWeight: 500 }}>
                                    Clear {independent.length}
                                </button>
                            )}
                        </div>
                    </Panel>

                    {/* Alerts */}
                    {errorMessage && (
                        <div style={{ display: 'flex', gap: 8, padding: '10px 14px', background: '#fef2f2',
                                       border: '1px solid #fecaca', borderRadius: 8 }}>
                            <X size={14} style={{ color: '#ef4444', flexShrink: 0, marginTop: 1 }} />
                            <span style={{ fontSize: 13, color: '#b91c1c' }}>{errorMessage}</span>
                        </div>
                    )}
                    {successMessage && !isRunning && !errorMessage && (
                        <div style={{ display: 'flex', gap: 8, padding: '10px 14px', background: '#f0fdf4',
                                       border: '1px solid #bbf7d0', borderRadius: 8 }}>
                            <Check size={14} style={{ color: '#22c55e', flexShrink: 0, marginTop: 1 }} />
                            <span style={{ fontSize: 13, color: '#15803d' }}>{successMessage}</span>
                        </div>
                    )}

                    {/* Results table */}
                    {hasResults && (
                        <Panel style={{ padding: 0, overflow: 'hidden' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                           padding: '12px 18px', background: INK }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <span style={{ fontSize: 12, fontWeight: 700, color: '#fff',
                                                   textTransform: 'uppercase', letterSpacing: '0.1em' }}>Results</span>
                                    {sample && (
                                        <span style={{ fontSize: 12, fontWeight: 700, color: AMBER,
                                                        background: 'rgba(231,162,51,0.18)', borderRadius: 999,
                                                        padding: '2px 10px' }}>n = {sample}</span>
                                    )}
                                    {totalFilters > 0 && (
                                        <span style={{ fontSize: 12, fontWeight: 700, color: LIME,
                                                        background: 'rgba(119,190,63,0.18)', borderRadius: 999,
                                                        padding: '2px 10px' }}>{totalFilters} filter{totalFilters !== 1 ? 's' : ''}</span>
                                    )}
                                </div>
                                <button type="button" onClick={handleExport}
                                    style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 500,
                                             color: '#fff', background: 'rgba(255,255,255,0.12)', border: 'none',
                                             borderRadius: 6, padding: '6px 12px', cursor: 'pointer' }}>
                                    <Download size={13} /> Export
                                </button>
                            </div>
                            <div style={{ overflowX: 'auto', maxHeight: 'calc(100vh - 220px)', overflowY: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                    <thead>
                                        <tr style={{ background: '#1a4a47', color: '#fff', position: 'sticky', top: 0 }}>
                                            <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11,
                                                          fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Driver</th>
                                            {analysisType === 'linear_regression' ? (<>
                                                <th style={{ padding: '10px 12px', textAlign: 'center', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Coeff.</th>
                                                <th style={{ padding: '10px 12px', textAlign: 'center', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Abs. Coeff.</th>
                                                <th style={{ padding: '10px 12px', textAlign: 'center', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Coeff. %</th>
                                                <th style={{ padding: '10px 12px', textAlign: 'center', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>R²</th>
                                            </>) : (<>
                                                <th style={{ padding: '10px 12px', textAlign: 'center', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Kruskal %</th>
                                                <th style={{ padding: '10px 12px', textAlign: 'center', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Lower</th>
                                                <th style={{ padding: '10px 12px', textAlign: 'center', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Raw</th>
                                                <th style={{ padding: '10px 12px', textAlign: 'center', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Upper</th>
                                                <th style={{ padding: '10px 12px', textAlign: 'center', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>R²</th>
                                            </>)}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {results.map((row, i) => {
                                            const isSummary = row.Driver === 'R-Squared Value';
                                            return (
                                                <tr key={i} style={{
                                                    background: isSummary ? '#edfde5' : i % 2 === 0 ? CARD : PAPER,
                                                    borderTop: `1px solid ${RULE}`,
                                                }}>
                                                    <td style={{ padding: '9px 16px', fontWeight: isSummary ? 700 : 400,
                                                                  color: isSummary ? TEAL : INK }}>
                                                        {row.Driver || '—'}
                                                    </td>
                                                    {analysisType === 'linear_regression' ? (<>
                                                        <td style={{ padding: '9px 12px', textAlign: 'center', color: MUTED, fontVariantNumeric: 'tabular-nums' }}>{row.Coefficient?.toFixed(2) ?? '—'}</td>
                                                        <td style={{ padding: '9px 12px', textAlign: 'center', color: MUTED, fontVariantNumeric: 'tabular-nums' }}>{row['Absolute Coefficient']?.toFixed(2) ?? '—'}</td>
                                                        <td style={{ padding: '9px 12px', textAlign: 'center' }}>
                                                            {row['Coefficient (%)'] != null ? (
                                                                <span style={{ background: '#eef2ff', color: '#4338ca', borderRadius: 6,
                                                                                padding: '2px 8px', fontSize: 12, fontWeight: 600 }}>
                                                                    {row['Coefficient (%)'].toFixed(2)}%
                                                                </span>
                                                            ) : '—'}
                                                        </td>
                                                        <td style={{ padding: '9px 12px', textAlign: 'center' }}>
                                                            {row['R-Squared'] != null ? (
                                                                <span style={{ background: '#f0fdf4', color: TEAL, borderRadius: 6,
                                                                                padding: '2px 8px', fontSize: 12, fontWeight: 700 }}>
                                                                    {(row['R-Squared'] * 100).toFixed(2)}%
                                                                </span>
                                                            ) : '—'}
                                                        </td>
                                                    </>) : (<>
                                                        <td style={{ padding: '9px 12px', textAlign: 'center' }}>
                                                            {row['Kruskal Importance (%)'] != null ? (
                                                                <span style={{ background: '#eef2ff', color: '#4338ca', borderRadius: 6,
                                                                                padding: '2px 8px', fontSize: 12, fontWeight: 600 }}>
                                                                    {row['Kruskal Importance (%)'].toFixed(2)}%
                                                                </span>
                                                            ) : '—'}
                                                        </td>
                                                        <td style={{ padding: '9px 12px', textAlign: 'center', color: MUTED, fontVariantNumeric: 'tabular-nums' }}>{row['Lower Confidence Bound (0.025)']?.toFixed(4) ?? '—'}</td>
                                                        <td style={{ padding: '9px 12px', textAlign: 'center', color: MUTED, fontVariantNumeric: 'tabular-nums' }}>{row['Raw Kruskal Importance']?.toFixed(4) ?? '—'}</td>
                                                        <td style={{ padding: '9px 12px', textAlign: 'center', color: MUTED, fontVariantNumeric: 'tabular-nums' }}>{row['Upper Confidence Bound (0.975)']?.toFixed(4) ?? '—'}</td>
                                                        <td style={{ padding: '9px 12px', textAlign: 'center' }}>
                                                            {row['R-Squared'] != null ? (
                                                                <span style={{ background: '#f0fdf4', color: TEAL, borderRadius: 6,
                                                                                padding: '2px 8px', fontSize: 12, fontWeight: 700 }}>
                                                                    {(row['R-Squared'] / 10).toFixed(2)}%
                                                                </span>
                                                            ) : '—'}
                                                        </td>
                                                    </>)}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </Panel>
                    )}
                </div>

                {/* ── Right sidebar: model statement ───────────────────── */}
                <aside style={{ width: 300, flexShrink: 0, position: 'sticky', top: 16, alignSelf: 'flex-start' }}>
                    <div style={{ background: INK, borderRadius: 12, overflow: 'hidden' }}>

                        {/* Equation */}
                        <div style={{ padding: '18px 18px 0' }}>
                            <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
                                           letterSpacing: '0.18em', color: LIME, marginBottom: 10 }}>
                                MODEL STATEMENT
                            </div>
                            <p style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12,
                                         lineHeight: 1.7, color: '#E4EEEB', margin: 0 }}>
                                {depVar
                                    ? <span style={{ color: '#fff' }}>{shortLabel(depVar.label || depVar.name)}</span>
                                    : <span style={{ color: '#7FA09A' }}>outcome</span>
                                }
                                <span style={{ color: LIME }}> ~ </span>
                                {independent.length === 0
                                    ? <span style={{ color: '#7FA09A' }}>drivers</span>
                                    : independent.map((id, i) => {
                                        const v = columns.find(c => c.name === id);
                                        return (
                                            <span key={id}>
                                                {i > 0 && <span style={{ color: LIME }}> + </span>}
                                                <span style={{ color: AMBER }}>β{i + 1}</span>
                                                <span style={{ color: '#7FA09A' }}>·</span>
                                                <span>{shortLabel(v?.label || id)}</span>
                                            </span>
                                        );
                                    })
                                }
                            </p>
                        </div>

                        {/* Stats strip */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
                                       gap: 1, background: '#124946', margin: '14px 0 0' }}>
                            {[
                                ['Method',  analysisType === 'linear_regression' ? 'Linear' : 'Kruskal'],
                                ['Drivers', String(independent.length)],
                                ['Filters', String(totalFilters)],
                            ].map(([k, v]) => (
                                <div key={k} style={{ background: INK, padding: '12px 14px' }}>
                                    <div style={{ fontSize: 10, color: '#7FA09A', letterSpacing: '0.1em',
                                                   textTransform: 'uppercase', fontFamily: "'IBM Plex Mono', monospace" }}>
                                        {k}
                                    </div>
                                    <div style={{ fontSize: 20, fontWeight: 700, color: '#fff', marginTop: 3 }}>
                                        {v}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Checklist */}
                        <div style={{ padding: '14px 18px', borderTop: '1px solid #124946' }}>
                            {checks.map(c => (
                                <div key={c.label} style={{ display: 'flex', alignItems: 'center',
                                                              gap: 10, marginBottom: 8, fontSize: 12 }}>
                                    <span style={{
                                        width: 16, height: 16, borderRadius: '50%', flexShrink: 0,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        background: c.done ? LIME : 'transparent',
                                        border: c.done ? 'none' : '1px solid #2C605C',
                                    }}>
                                        {c.done && <Check size={9} strokeWidth={3} color={INK} />}
                                    </span>
                                    <span style={{ color: c.done ? '#E4EEEB' : '#7FA09A' }}>{c.label}</span>
                                </div>
                            ))}
                        </div>

                        {/* Run button */}
                        <div style={{ padding: '0 16px 16px' }}>
                            <button type="button" onClick={handleSubmit}
                                disabled={!ready || isRunning}
                                style={{
                                    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    gap: 8, padding: '12px 0', fontSize: 13, fontWeight: 700, borderRadius: 8,
                                    border: 'none', cursor: ready && !isRunning ? 'pointer' : 'not-allowed',
                                    background: ready ? LIME : '#124946',
                                    color: ready ? INK : '#5E817C',
                                    transition: 'all 0.2s',
                                }}>
                                {isRunning
                                    ? <><svg width="14" height="14" fill="none" viewBox="0 0 24 24"
                                              style={{ animation: 'daf-spin 0.9s linear infinite', transformOrigin: 'center' }}>
                                            <style>{`@keyframes daf-spin{to{transform:rotate(360deg)}}`}</style>
                                            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" opacity="0.25"/>
                                            <path fill="currentColor" opacity="0.8" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                                        </svg> Running…</>
                                    : ready
                                        ? <><Zap size={14} /> Run analysis</>
                                        : <><Lock size={13} /> Finish the spec to run</>
                                }
                            </button>
                            <p style={{ margin: '8px 0 0', textAlign: 'center', fontSize: 11, color: '#7FA09A' }}>
                                {ready
                                    ? 'Results appear below. Nothing is overwritten.'
                                    : 'Add an outcome and at least two drivers.'}
                            </p>
                        </div>
                    </div>

                    {/* Selected driver chips */}
                    {independent.length > 0 && (
                        <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {independent.map(id => {
                                const v = columns.find(c => c.name === id);
                                return (
                                    <button key={id} type="button" onClick={() => toggle(id)}
                                        style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11,
                                                  padding: '4px 8px', borderRadius: 6, cursor: 'pointer',
                                                  background: CARD, border: `1px solid ${RULE}`, color: TEAL }}>
                                        {shortLabel(v?.label || id)}
                                        <X size={11} />
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {/* Reset */}
                    <button type="button" onClick={reset}
                        style={{ width: '100%', marginTop: 10, display: 'flex', alignItems: 'center',
                                  justifyContent: 'center', gap: 6, fontSize: 12, fontWeight: 500,
                                  padding: '8px 0', borderRadius: 8, cursor: 'pointer', color: TEAL,
                                  border: `1px solid ${RULE}`, background: 'transparent' }}>
                        <RotateCcw size={13} /> Start over
                    </button>
                </aside>
            </div>
        </>
    );
};

export default DriverAnalysisForm;
